/*
 * Productivity rule matching, mirroring the BACKEND exactly.
 *
 * The whole point of the pattern tester is to answer "would this rule catch
 * that session", so it has to agree with the code that actually classifies. The
 * live matcher is websiteAppUsageController.getProductivityStatus:
 *
 *   non-regex   rule.pattern === appName      exact, case-SENSITIVE
 *   regex       new RegExp(rule.pattern)      no flags: case-SENSITIVE, unanchored
 *   priority    domain rules, then website, then application — first match wins
 *   team        a teamRules entry for the user's team beats globalStatus
 *   bad regex   caught and logged; the rule then matches NOTHING, silently
 *
 * The markup's tester got two of these wrong — it lower-cased both sides for a
 * plain pattern (making it a case-insensitive SUBSTRING test) and passed the "i"
 * flag for a regex. A rule the tester called a match would classify nothing at
 * all, which is worse than having no tester: it is a confident wrong answer
 * about the one thing this page exists to get right.
 */

export const RULE_TYPES = ["domain", "website", "application"];

/** Priority order the backend collects rules in. Earlier wins. */
const TYPE_PRIORITY = { domain: 0, website: 1, application: 2 };

/**
 * @returns {{ ok: true } | { ok: false, message: string }}
 * A pattern that cannot compile is not a warning — the rule it belongs to never
 * fires, and nothing tells you.
 */
export function validatePattern(pattern, isRegex) {
    if (!pattern) return { ok: false, message: "Enter a pattern" };
    if (!isRegex) return { ok: true };

    try {
        new RegExp(pattern);
        return { ok: true };
    } catch (error) {
        return { ok: false, message: error.message };
    }
}

/** Does this rule match this sample? Same rules as the server. */
export function matches(rule, sample) {
    if (!sample) return false;

    if (rule.isRegex) {
        try {
            // No flags, deliberately — the backend has none either, so a rule
            // written for "YouTube" does not catch "youtube".
            return new RegExp(rule.pattern).test(sample);
        } catch {
            return false; // an uncompilable pattern matches nothing
        }
    }

    return rule.pattern === sample;
}

/**
 * Which rule would win for this sample, and what status the given team gets.
 *
 * Used by the tester to answer the question people actually have — not "does my
 * pattern match" but "will this session be classified the way I expect", which
 * a higher-priority rule elsewhere can change.
 */
export function resolve(rules, sample, teamId = null) {
    const ordered = [...rules].sort(
        (a, b) => (TYPE_PRIORITY[a.type] ?? 9) - (TYPE_PRIORITY[b.type] ?? 9),
    );

    for (const rule of ordered) {
        if (!matches(rule, sample)) continue;

        const override = teamId
            ? (rule.teamRules ?? []).find((tr) => idOf(tr.teamId) === teamId)
            : null;

        return {
            rule,
            status: override ? override.status : rule.globalStatus,
            viaOverride: Boolean(override),
        };
    }

    // Nothing matched: the backend leaves it unrated.
    return { rule: null, status: "unrated", viaOverride: false };
}

export const idOf = (value) => (typeof value === "string" ? value : (value?._id ?? null));

/** The effective status for one team, for the "By team" view. */
export function statusForTeam(rule, teamId) {
    const override = (rule.teamRules ?? []).find((tr) => idOf(tr.teamId) === teamId);
    return {
        status: override ? override.status : rule.globalStatus,
        viaOverride: Boolean(override),
    };
}

export function ruleStats(rules) {
    const broken = rules.filter((r) => !validatePattern(r.pattern, r.isRegex).ok);

    return {
        total: rules.length,
        productive: rules.filter((r) => r.globalStatus === "productive").length,
        unproductive: rules.filter((r) => r.globalStatus === "unproductive").length,
        withOverrides: rules.filter((r) => (r.teamRules ?? []).length > 0).length,
        // Rules that can never fire. Counted separately because they look
        // completely normal in the table.
        broken: broken.length,
        brokenRules: broken,
    };
}

export const EMPTY_FILTERS = { search: "", type: "", status: "", category: "" };

export function filterRules(rules, filters) {
    const q = filters.search.trim().toLowerCase();

    return rules.filter((rule) => {
        if (filters.type && rule.type !== filters.type) return false;
        if (filters.status && rule.globalStatus !== filters.status) return false;
        if (filters.category && (rule.category || "") !== filters.category) return false;

        if (q) {
            const haystack = `${rule.name} ${rule.pattern} ${rule.category ?? ""}`;
            if (!haystack.toLowerCase().includes(q)) return false;
        }
        return true;
    });
}

export const RULE_SORTS = {
    name: (r) => r.name.toLowerCase(),
    type: (r) => TYPE_PRIORITY[r.type] ?? 9,
    pattern: (r) => r.pattern.toLowerCase(),
    status: (r) => r.globalStatus,
    overrides: (r) => (r.teamRules ?? []).length,
};

/**
 * Parse a pasted JSON export into rules the API will accept.
 *
 * Returns the valid rules AND the reasons for every rejection, rather than
 * failing on the first bad entry: an import of forty rules where one has a typo
 * should tell you which one.
 */
export function parseImport(text, existing = []) {
    let data;
    try {
        data = JSON.parse(text);
    } catch (error) {
        return { rules: [], errors: [`That is not valid JSON — ${error.message}`], duplicates: [] };
    }

    const list = Array.isArray(data) ? data : data?.rules;
    if (!Array.isArray(list)) {
        return {
            rules: [],
            errors: ['Expected an array of rules, or an object with a "rules" array.'],
            duplicates: [],
        };
    }

    const seen = new Set(existing.map((r) => `${r.type}::${r.pattern}`));
    const rules = [];
    const errors = [];
    const duplicates = [];

    list.forEach((raw, i) => {
        const where = `Rule ${i + 1}${raw?.name ? ` (${raw.name})` : ""}`;

        if (!raw?.name) return errors.push(`${where}: missing a name`);
        if (!RULE_TYPES.includes(raw.type)) {
            return errors.push(`${where}: type must be domain, website or application`);
        }
        if (!raw.pattern) return errors.push(`${where}: missing a pattern`);

        const valid = validatePattern(raw.pattern, Boolean(raw.isRegex));
        if (!valid.ok) return errors.push(`${where}: ${valid.message}`);

        const key = `${raw.type}::${raw.pattern}`;
        if (seen.has(key)) return duplicates.push(`${where}: already exists`);
        seen.add(key);

        rules.push({
            name: raw.name,
            type: raw.type,
            pattern: raw.pattern,
            isRegex: Boolean(raw.isRegex),
            globalStatus: raw.globalStatus ?? "unrated",
            category: raw.category ?? "",
        });
    });

    return { rules, errors, duplicates };
}
